import apiClient from './apiClient';

export interface Transaction {
  id: string;
  user: string;
  phone: string;
  type: 'payment' | 'refund' | 'withdrawal' | 'commission';
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  reference: string;
  created_at: string;
  completed_at?: string;
}

export interface TransactionListResponse {
  count: number;
  transactions: Transaction[];
}

export interface WithdrawalRequest {
  id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  amount: number;
  convenience_fee: number;
  net_amount: number;
  bank_name: string;
  account_number: string;
  account_name: string;
  status: 'pending' | 'completed' | 'failed';
  is_first_daily_withdrawal: boolean;
  transfer_reference: string | null;
  created_at: string;
  processed_at: string | null;
}

export interface WithdrawalListResponse {
  count: number;
  withdrawals: WithdrawalRequest[];
}

class PaymentService {
  async getTransactions(
    status?: string,
    type?: string,
    limit: number = 50
  ): Promise<TransactionListResponse> {
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (type) params.append('type', type);
      params.append('limit', limit.toString());

      const response = await apiClient.get(`/admin/payments/`, {
        params: Object.fromEntries(params),
      });

      return response.data.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  async getRevenueMetrics() {
    try {
      const response = await apiClient.get(`/admin/payments/revenue/`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching revenue metrics:', error);
      return {
        today: 0,
        week: 0,
        month: 0,
      };
    }
  }

  async getWithdrawals(status: string = 'pending'): Promise<WithdrawalListResponse> {
    try {
      const response = await apiClient.get(`/admin/withdrawals/`, {
        params: { status },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      throw error;
    }
  }

  async approveWithdrawal(id: string): Promise<void> {
    const response = await apiClient.post(`/admin/withdrawals/${id}/approve/`);
    return response.data;
  }

  async rejectWithdrawal(id: string, reason?: string): Promise<void> {
    const response = await apiClient.post(`/admin/withdrawals/${id}/reject/`, {
      reason: reason || '',
    });
    return response.data;
  }

  async getArtisanBankAccount(userId: string): Promise<ArtisanBankAccount | null> {
    const response = await apiClient.get(`/admin/users/${userId}/bank-account/`);
    return response.data?.data?.bank_account ?? null;
  }

  async verifyArtisanBankAccount(userId: string): Promise<ArtisanBankAccount> {
    const response = await apiClient.post(`/admin/users/${userId}/bank-account/verify/`);
    return response.data?.data?.bank_account;
  }
}

export interface ArtisanBankAccount {
  id: string;
  bank_name: string;
  bank_code: string;
  account_number: string;
  account_holder_name: string;
  is_verified: boolean;
  payout_status: 'pending_verification' | 'verified' | 'verification_failed' | 'disabled';
  paystack_recipient_code: string | null;
  verification_error: string;
  last_verified_at: string | null;
  verification_attempts: number;
}

export default new PaymentService();
